import { CONVEYANCE_TYPES, NotModified, type Strings } from '@koala/sdk';
import {
  getBasket,
  removePickupTime,
  getPickupTimeByBasket,
  getPickupTime,
  setPickupTime,
} from '@koala/sdk/v4';
import { call, put, select, takeLatest, type SagaReturnType } from 'redux-saga/effects';
import basketActions from '../basket/actions';
import { BASKET_ERROR } from '../basket/messages';
import globalActions from '../global/actions';
import orderStatusActions from '../orderStatus/actions';
import conveyanceModeActions from '../conveyanceMode/actions';
import paymentActions from '../payment/actions';
import actions from './actions';
import { DATE_FORMAT } from '@/constants/dates';
import { ASAP_VALUE, TIME_MODES, TIMESLOT_TYPES } from '@/constants/global';
import { createHttpClient } from '@/services/client';
import { type RootState } from '@/types/app';
import { getOrigin } from '@/utils';
import { getIdsFromState } from '@/utils/checkout';
import { getFormattedDateInTimezone } from '@/utils/dates';
import { basketReportsNoGiftCard } from '@/utils/giftCard';
import { prepareErrorMessage } from '@/utils/global';
import { safelyGetString } from '@/utils/stringHelpers';
import * as ErrorReporter from '@/utils/errorReporter';
import {
  getRestaurantUnavailableReason,
  getUnavailableBasketItemIds,
  isRestaurantUnavailableError,
} from '@/utils/errors';
import { ERROR_MESSAGES } from '@/constants/events';
import { isStatusError, GATEWAY_STATUSES } from '@/utils/networkErrors';
import { retryGuardedReadSaga } from '@/utils/withJsonGuard';

function* fetchHandoffTimesByLocationSaga(
  action: ReturnType<typeof actions.fetchHandoffTimesByLocation>,
) {
  if (!action.locationId) {
    return null;
  }
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const response: SagaReturnType<typeof getPickupTime> = yield call(
      getPickupTime,
      {
        locationId: action.locationId,
        wantedAtType: action.hoursType,
        dayWanted: action.wantedAtDay,
      },
      { client },
    );

    // Include asap here to avoid mutations downstream
    if (action.includeAsap) {
      response.unshift(TIME_MODES.ASAP);
    }

    yield put(
      actions.fetchHandoffTimesSuccess(TIMESLOT_TYPES.LOCATION_TIMES, response, action.wantedAtDay),
    );
  } catch (error) {
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      BASKET_ERROR.getPickupTimesSaga,
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));
    yield put({ type: actions.FETCH_HANDOFF_TIMES_FAILURE });

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_HANDOFF_TIMES_BY_LOCATION_ERROR,
    });
  }
}
// This was removed from the order flow when it should NOT
// INVESTIGATE MORE We should probably stop calling this after initializeOrder saga flow
// This getPickupTime data is never used because we removed fetching timeslot when removing asap originally
// we use useHandoffTimes hook to get times instead
function* fetchHandoffTimesByBasketSaga(
  action: ReturnType<typeof actions.fetchHandoffTimesByBasket>,
) {
  if (!action.locationId) {
    return null;
  }
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const response: SagaReturnType<typeof getPickupTimeByBasket> = yield call(
      getPickupTimeByBasket,
      {
        basketId: action.basketId,
        locationId: action.locationId,
        hoursType: action.hoursType,
        dayWanted: action.wantedAtDay,
      },
      { client },
    );

    // Include asap here to avoid mutations downstream
    if (action.includeAsap) {
      response.unshift(TIME_MODES.ASAP);
    }

    // Optionally attempt to default set the wanted at time as the first item
    // in the array. This occurs if a user has manually toggled
    // to a new day in the checkout day dropdown.
    if (action.previousAction !== orderStatusActions.POST_INITIALIZE_ORDER) {
      if (response[0] === TIME_MODES.ASAP) {
        yield put(orderStatusActions.wantedAtBasketRemove());
      } else if (response[0]) {
        yield put(orderStatusActions.wantedAtBasketSet(response[0]));
      } else {
        // We have no new valid response times--most likely the store is closed.
        // Throw an error to stop execution of this saga, revert to previous wanted at time + basket times,
        // and display a toast error to notify the user.
        throw new NotModified();
      }
    }

    // Store handoff times
    yield put(
      actions.fetchHandoffTimesSuccess(TIMESLOT_TYPES.BASKET_TIMES, response, action.wantedAtDay),
    );

    if (action.previousAction === orderStatusActions.POST_INITIALIZE_ORDER) {
      // Wanted at is either set or it is ASAP
      const wantedAt = action.wantedAt || TIME_MODES.ASAP;

      // Ensure the wanted at time is available in the response.
      // If not, attempt to set the first item in the response.
      // Do not set if the first item is ASAP, as this is
      // already the current state of the basket
      if (!response.includes(wantedAt)) {
        // Lets also ensure we're not trying to set ASAP and
        // there's actually availability for the selected day
        if (response[0] !== TIME_MODES.ASAP && response[0]) {
          //yield put(orderStatusActions.wantedAtBasketSet(response[0]));
        }
      }
    }
  } catch (error) {
    if (error instanceof NotModified) {
      const wantedAtDay = getFormattedDateInTimezone(
        action.wantedAtDay,
        DATE_FORMAT.WRITTEN_OUT_DATE,
        '+00:00',
      );
      const strings: Strings = yield select((state: RootState) => state.app.cmsConfig.strings);
      const errorString = safelyGetString(strings, 'handoff_time.no_available_basket_times', {
        date: wantedAtDay,
      });

      yield put(globalActions.displayErrorToast(errorString, false));
    } else {
      const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
        prepareErrorMessage,
        BASKET_ERROR.getPickupTimesSaga,
        error,
      );
      yield put(globalActions.displayErrorToast(errorResponse.message, true));

      ErrorReporter.captureException(error, 'error', {
        errorResponse,
        eventName: ERROR_MESSAGES.FETCH_HANDOFF_TIMES_BY_BASKET_ERROR,
      });
    }

    yield put({ type: actions.FETCH_HANDOFF_TIMES_FAILURE });
  }
}

/**
 * After a refused time change, re-reads the basket and follows its gift card. (TEAM-15105)
 *
 * coo-api (TEAM-15104) can release the card and still refuse the change. When chowly-api refuses a
 * slot only over the card's window, coo-api releases the card and retries the set once. If that
 * retry fails too, coo-api returns the retry's error, and the error carries no basket. Without a
 * re-read, redux keeps the card and Place Order sends one the basket no longer holds, so the order
 * is refused with a 400.
 *
 * Runs only when a card is applied, and before the caller settles the checkout and shows the
 * refusal (see the call site). The read is retried like any other read
 * (docs/COO_API_FAILURE_HANDLING.md, Layer 1), since the blip that failed coo-api's retry can hit it
 * too. A read that still fails is reported and changes nothing, and the diner still sees the
 * original error. Nothing here may throw: it runs inside the caller's catch, and an escaping error
 * would end the takeLatest watcher.
 */
function* followGiftCardAfterRefusedTimeChange() {
  try {
    const state = (yield select()) as RootState;
    if (!state.app.payment.storedValue) {
      return;
    }

    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const { basketId, locationId } = getIdsFromState(state);
    const basket: SagaReturnType<typeof getBasket> = yield* retryGuardedReadSaga(
      () => getBasket({ basketId, locationId }, { client }),
      {
        urlHint: '/v1/ordering/store-locations/{locationId}/baskets/{basketId}',
        basketId,
        locationId: locationId?.toString(),
      },
    );

    // The same two steps as the success path below: the basket first, so the summary's gift-card
    // line reads the real amount, then the card the basket no longer holds.
    yield put(basketActions.success(basket));
    if (basketReportsNoGiftCard(basket)) {
      yield put(paymentActions.storedValueClear());
    }
  } catch (error) {
    ErrorReporter.captureException(error, 'warning', {
      eventName: ERROR_MESSAGES.REFETCH_BASKET_AFTER_PICKUP_TIME_ERROR,
    });
  }
}

export function* wantedAtBasketSetSaga(
  action: ReturnType<typeof orderStatusActions.wantedAtBasketSet>,
) {
  try {
    const state = (yield select()) as RootState;
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const conveyanceMode = state.app.conveyanceMode;
    const { basketId, locationId } = getIdsFromState(state);
    const response = (yield call(
      setPickupTime,
      { basketId, locationId, wanted_at: action.wantedAt },
      { client },
    )) as SagaReturnType<typeof setPickupTime>;

    // Success
    yield put(orderStatusActions.orderPending());
    yield put(basketActions.success(response));
    // TEAM-15105: coo-api (TEAM-15104) evicts the gift card server-side when the new slot is
    // outside the location's gift-card window. Validate only ever sets storedValue and submit
    // builds gift_card from redux, so redux has to follow the server's response here.
    if (basketReportsNoGiftCard(response) && state.app.payment.storedValue) {
      yield put(paymentActions.storedValueClear());
    }
    yield put(
      conveyanceModeActions.setWantedTime(
        action.wantedAt,
        Number(response.location.id),
        conveyanceMode.type,
      ),
    );
    // Keep basket.fulfillment in sync with checkoutBasket.wanted_at so a return
    // to the menu page doesn't re-sync conveyanceMode from a stale fulfillment.
    const currentFulfillment = state.app.basket.fulfillment;
    yield put(
      basketActions.updateBasketFulfillment({
        ...currentFulfillment,
        type: conveyanceMode.type,
        time_wanted: action.wantedAt,
        address:
          conveyanceMode.type === CONVEYANCE_TYPES.DELIVERY && currentFulfillment?.address
            ? { ...currentFulfillment.address, time_wanted: action.wantedAt }
            : currentFulfillment?.address,
      }),
    );

    // Confirm the applied time, if the caller asked for it. This has to happen here
    // rather than where the action was dispatched: setPickupTime is a network call
    // behind takeLatest, so the component never learns the outcome and would confirm
    // a commit that then failed into the inline item-unavailable notice below
    // (TEAM-14387 AC6, TEAM-13726). The internal setWantedTime dispatch above carries
    // no confirmation, so this cannot double-fire.
    // Scope of this guarantee, stated precisely: the commit itself has succeeded by
    // this point, and a failure *in this saga* cannot precede the toast. It does not
    // cover the menu refresh the second line mentions. refreshBasketMenu is dispatched,
    // not awaited, and its own recovery branch (redux/menu/saga.ts:72-89) responds to a
    // wanted-at validation error by dispatching setWantedTime(undefined) — clearing the
    // very time just confirmed. That window is narrow and the alternative (toasting from
    // the refresh's success) would not fire at all when no locationId is present, so it
    // is accepted rather than closed. Do not read the comment above as "the toast implies
    // the refresh completed".
    if (action.confirmation) {
      yield put(
        globalActions.displayToast(
          action.confirmation.message,
          undefined,
          undefined,
          action.confirmation.secondaryMessage,
        ),
      );
    }
  } catch (error) {
    // We must revert the timeslot state if the attempted time setting fails
    const state = (yield select()) as RootState;
    yield put(actions.setWantedAt(state.app.basket.checkoutBasket?.wanted_at, true));

    // TEAM-15105: before orderPending, so the checkout is still UPDATING while the basket is
    // re-read. That keeps Place Order and the gift-card form disabled, and keeps the mutation
    // guard holding other basket writes, so the re-read can't land over a newer basket.
    yield call(followGiftCardAfterRefusedTimeChange);

    yield put(orderStatusActions.orderPending());

    // TEAM-14925: a transient gateway 502 on the wanted-time set call should show the
    // cart-preserving recovery modal, not the dead-end error banner. Must come before
    // any other error classification so a genuine 502 is never misrouted.
    // The revert above runs first so the UI reflects that the time didn't stick.
    if (isStatusError(error, GATEWAY_STATUSES)) {
      yield put(orderStatusActions.recovery502Triggered('wanted_time'));
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.SET_PICKUP_TIME_ERROR,
      });
      return;
    }

    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      BASKET_ERROR.setPickupTimeSaga,
      error,
    );

    const unavailableBasketItemIds = getUnavailableBasketItemIds(errorResponse);
    if (unavailableBasketItemIds.length) {
      yield put(
        basketActions.setUnavailableBasketItems(
          unavailableBasketItemIds,
          errorResponse.error?.error,
        ),
      );
      yield put(conveyanceModeActions.setAttemptedWantedAt(action.wantedAt));
      // TEAM-13726: the inline item-unavailable notice (partial- or full-removal)
      // is the single source of notification for a scheduled-time unavailability, so
      // suppress the redundant red status-bar error that duplicates it — this also
      // covers the "Basket is not valid" message when the time-driven removal empties
      // the cart (AC #7). Unrelated errors (no unavailable items) still notify below.
      yield put(orderStatusActions.orderStatusMessageClear());
    } else {
      yield put(orderStatusActions.orderStatusMessageErrorSet(errorResponse.message));
    }

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.SET_PICKUP_TIME_ERROR,
    });
  }
}

export function* wantedAtBasketRemoveSaga(
  action: ReturnType<typeof orderStatusActions.wantedAtBasketRemove>,
) {
  try {
    const state = (yield select()) as RootState;
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const conveyanceMode = state.app.conveyanceMode;
    const { basketId, locationId } =
      action.basketId && action.locationId
        ? { basketId: action.basketId, locationId: action.locationId }
        : getIdsFromState(state);
    const response = (yield call(
      removePickupTime,
      { basketId, locationId },
      { client },
    )) as SagaReturnType<typeof removePickupTime>;

    yield put(orderStatusActions.orderPending());
    yield put(basketActions.success(response));

    // The server no longer holds a time. initializeOrderSaga's ASAP gate (TEAM-14375) reads
    // this as its witness that a DELETE is still needed, so leaving it set here would make
    // every later checkout entry re-fire the call that gate exists to skip.
    yield put(actions.setWantedAt(null));

    yield put(
      conveyanceModeActions.setWantedTime(
        ASAP_VALUE,
        Number(response.location.id),
        conveyanceMode.type,
      ),
    );
    // Keep basket.fulfillment in sync with the ASAP change so a return to the
    // menu page doesn't re-sync conveyanceMode from a stale fulfillment.
    const currentFulfillment = state.app.basket.fulfillment;
    yield put(
      basketActions.updateBasketFulfillment({
        ...currentFulfillment,
        type: conveyanceMode.type,
        time_wanted: undefined,
        address:
          conveyanceMode.type === CONVEYANCE_TYPES.DELIVERY && currentFulfillment?.address
            ? { ...currentFulfillment.address, time_wanted: '' }
            : currentFulfillment?.address,
      }),
    );
  } catch (error) {
    // We must revert the timeslot state if the attempted time setting fails
    const state = (yield select()) as RootState;
    yield put(actions.setWantedAt(state.app.basket.checkoutBasket?.wanted_at, true));

    // TEAM-14925: a transient gateway 502 on the wanted-time remove call should show
    // the cart-preserving recovery modal, not the dead-end error banner. Must come
    // before any other error classification (including restaurant-unavailable) so a
    // genuine 502 is never misrouted.
    // The revert above runs first so the UI reflects that the time didn't stick.
    if (isStatusError(error, GATEWAY_STATUSES)) {
      yield put(orderStatusActions.recovery502Triggered('wanted_time'));
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.REMOVE_PICKUP_TIME_ERROR,
      });
      return;
    }

    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      BASKET_ERROR.setPickupTimeSaga,
      error,
    );

    // TEAM-14642: this catch was the one basket-failure path with no check of any
    // kind — it re-armed the submit control unconditionally, before the error was
    // parsed at all, even when the restaurant had stopped accepting orders. Parse
    // first, then decide: a restaurant-unavailable refusal is terminal for this
    // checkout, so apply the blocking state and do NOT dispatch orderPending().
    if (isRestaurantUnavailableError(errorResponse)) {
      yield put(
        orderStatusActions.restaurantUnavailable(getRestaurantUnavailableReason(errorResponse)),
      );

      // Still reported, at 'info' rather than 'error'. This is a known diner-facing
      // condition surfaced by the modal, so it is not an unhandled exception — but it
      // must stay COUNTABLE: TEAM-14421 re-queries this signal against a pinned Sentry
      // baseline, and TEAM-14642 names it as its measurement of record. Dropping it
      // would make "diners stopped hitting this" indistinguishable from "we stopped
      // counting it".
      //
      // A downgrade works here where it would NOT have worked in initializeOrderSaga
      // (PR #820 had to drop the capture outright): Sentry fingerprints by stack and
      // message, and this path reports under its own REMOVE_PICKUP_TIME_ERROR event,
      // so it groups separately from COO-WEBAPP-3ST either way. Follows the house
      // pattern in redux/menu/saga.ts:77 and utils/errorReporter.ts:90 — reduce the
      // level, keep the signal, say what it is.
      ErrorReporter.captureException(error, 'info', {
        errorResponse,
        eventName: ERROR_MESSAGES.REMOVE_PICKUP_TIME_ERROR,
        handled: 'restaurant-unavailable — surfaced via the service-unavailable modal',
      });
      return;
    }

    yield put(orderStatusActions.orderPending());

    const unavailableBasketItemIds = getUnavailableBasketItemIds(errorResponse);
    if (unavailableBasketItemIds.length) {
      yield put(
        basketActions.setUnavailableBasketItems(
          unavailableBasketItemIds,
          errorResponse.error?.error,
        ),
      );
      yield put(conveyanceModeActions.setAttemptedWantedAt(ASAP_VALUE));
      // TEAM-13726: the inline item-unavailable notice (partial- or full-removal)
      // is the single source of notification for a scheduled-time unavailability, so
      // suppress the redundant red status-bar error that duplicates it — this also
      // covers the "Basket is not valid" message when the time-driven removal empties
      // the cart (AC #7). Unrelated errors (no unavailable items) still notify below.
      yield put(orderStatusActions.orderStatusMessageClear());
    } else {
      yield put(orderStatusActions.orderStatusMessageErrorSet(errorResponse.message));
    }

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.REMOVE_PICKUP_TIME_ERROR,
    });
  }
}

export default function* rootSaga() {
  yield takeLatest(actions.FETCH_HANDOFF_TIMES_BY_BASKET, fetchHandoffTimesByBasketSaga);
  yield takeLatest(actions.FETCH_HANDOFF_TIMES_BY_LOCATION, fetchHandoffTimesByLocationSaga);

  yield takeLatest(orderStatusActions.WANTED_AT_BASKET_SET, wantedAtBasketSetSaga);
  yield takeLatest(orderStatusActions.WANTED_AT_BASKET_REMOVE, wantedAtBasketRemoveSaga);
}
